import React, { useEffect, useState } from "react";
import { useParams, useRouteMatch, useHistory } from "react-router-dom";
import { scroller, Element } from "react-scroll";
import {
  createComponentStore,
  useStoreActions,
  useStoreState,
} from "easy-peasy";
import {
  Descriptions,
  Spin,
  PageHeader,
  Dropdown,
  Menu,
  Button,
  Modal,
  Typography,
  message,
} from "antd";
import { ContractStoreModel, ACTION_TYPES } from "store/models/contracts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import ContractConditionList from "../components/conditions";
import CashFlow from "../components/cash-flow";
import CashFlowSummary from "../components/cash-flow/summary";
import Documents from "Components/form/documents-list";
import ContractRentableAreas from "./components/rentable-areas";
import ContractPayments from "./components/payments";
import ContractInterests from "./components/interest";
import StateTag from "Components/state-tag";
import { DISPLAY_MODE } from "Constants";
import { RULES } from "Constants/rules";
import ContractStatusTag from "../components/status-tag";
import EditButton from "./components/actions/edit";
import { AuthCheck, AUTH_ACTIONS } from "authorization";
import AbolishButton from "./components/actions/abolish";
import { GenerateBackPath, isScreenMobile } from "Utils";
import AddPaymentButton from "./components/actions/add-payment";
import FourOThree from "Pages/private/403";
import DeleteButton from "./components/actions/delete";
import FourOFour from "Pages/private/404";

const { Title } = Typography;

const useContractStore = createComponentStore(ContractStoreModel);

export default function ContractDetailsPage({ archived = false, ...props }) {
  const { abilities } = useStoreState((store) => store.auth);
  const IsAuthorized = AuthCheck(
    abilities,
    archived ? AUTH_ACTIONS.READ_ARCHIVE : AUTH_ACTIONS.READ,
    "Sözleşmeler"
  );
  const [store, actions] = useContractStore();

  const { updateBreadcrumb } = useStoreActions((store) => store.app);

  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  const [paymentFormVisibility, setPaymentFormVisibility] = useState(false);
  const [payments, setPayments] = useState([]);
  let { id } = useParams();
  let history = useHistory();
  let match = useRouteMatch();

  const rootPath = GenerateBackPath(match.path, 4);

  useEffect(() => {
    if (!IsAuthorized) return;
    if (archived) actions.fetchDeletedRecord({ id: id });
    else actions.fetchSingle({ id: id });
  }, [id, archived]);

  useEffect(() => {
    if (store.record) {
      setPayments(store.record.payments);
      updateBreadcrumb({
        object: {
          name: "Sözleşme " + store.record.code,
          path: match.path,
        },
        index: 2,
      });
    }
  }, [store.record]);

  useEffect(() => {
    console.log(store.process);
    if (store.process === ACTION_TYPES.UPDATED_STATU) {
      actions.fetchSingle({ id: id });
      message.success("Sözleşme durumu güncellendi.");
    }

    if (store.process === ACTION_TYPES.RESTORED_DATA) {
      history.push(`${rootPath}/${id}/detay`);
      message.success("Sözleşme arşivden geri alındı.");
    }

    if (store.process === ACTION_TYPES.REMOVED_DATA) {
      history.push(`${rootPath}/${id}/arsiv`);
      message.success("Sözleşme silindi.");
    }
  }, [store.process]);

  const IsLoading = (process) => {
    return [ACTION_TYPES.FETCHING_DATA].includes(process);
  };

  const handleNavigation = (key) => {
    scroller.scrollTo(key, { smooth: true, offset: -136 });
  };

  return store.process === ACTION_TYPES.FINISHED && !store.record ? (
    <FourOFour />
  ) : (
    <>
      <PageHeader
        className={
          isScreenMobile() ? "mobile-customized customized" : "customized"
        }
        backIcon={<FontAwesomeIcon icon={["fas", "chevron-left"]} />}
        onBack={() => {
          history.push(rootPath);
        }}
        title={
          <>
            {`Sözleşme - ${
              store.record?.code ||
              (IsAuthorized ? "Yükleniyor" : "Erişim Engllendi")
            }`}
            <div style={{ marginLeft: 16, display: "inline-block" }}>
              <ContractStatusTag contractStatu={store.record?.contractStatu} />
            </div>
            <StateTag
              state={store.record?.state}
              style={{ marginLeft: 4, display: "inline-block" }}
            />
          </>
        }
        extra={[
          <EditButton key={1} record={store.record} />,
          <AddPaymentButton
            key={2}
            record={store.record}
            onClick={() => setPaymentFormVisibility(true)}
          />,
          <AbolishButton
            key={3}
            record={store.record}
            actions={actions}
            loading={store.process === ACTION_TYPES.UPDATING_STATU}
          />,
          <DeleteButton
            key={4}
            record={store.record}
            actions={actions}
            loading={
              store.process === ACTION_TYPES.REMOVING_DATA ||
              store.process === ACTION_TYPES.RESTORING_DATA
            }
          />,
          <Dropdown
            key={5}
            trigger="click"
            overlay={
              <Menu onClick={(item) => handleNavigation(item.key)}>
                <Menu.Item key="1">Genel</Menu.Item>
                <Menu.Item key="2">Kiracı</Menu.Item>
                <Menu.Item key="3">Kiralanan Alanlar</Menu.Item>
                <Menu.Item key="4">Koşullar</Menu.Item>
                <Menu.Item key="5">Nakit Akışı</Menu.Item>
                {store.record?.type?.rule?.code === RULES.RULE0001 ? null : (
                  <Menu.Item key="6">Tahsilatlar</Menu.Item>
                )}
                {store.record?.type?.rule?.code === RULES.RULE0001 ? null : (
                  <Menu.Item key="7">Borç Bilgisi</Menu.Item>
                )}
                <Menu.Item key="8">Ekler</Menu.Item>
              </Menu>
            }
          >
            <Button>
              <FontAwesomeIcon icon={["fas", "ellipsis-h"]} />
            </Button>
          </Dropdown>,
        ]}
      />
      {IsAuthorized ? (
        <Spin spinning={IsLoading(store.process)}>
          <div style={{ margin: "0 24px" }}>
            <Element name="1">
              <Title level={4} style={{ marginTop: 24 }}>
                Genel
              </Title>
              <Descriptions size="small">
                <Descriptions.Item label="Sözleşme Türü">
                  {store.record?.type?.description}
                </Descriptions.Item>
                <Descriptions.Item label="Başlangıç Tarihi">
                  {moment(store.record?.dateOfBeginning).format("DD.MM.YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label="Bitiş Tarihi">
                  {moment(store.record?.dateOfFinish).format("DD.MM.YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label="Sözleşme Uzatma Kuralı">
                  {store.record?.extensionRule?.description}
                </Descriptions.Item>
                <Descriptions.Item label="Yer Teslim Tarihi">
                  {store.record?.dateOfPlaceDelivery
                    ? moment(store.record?.dateOfPlaceDelivery).format(
                        "DD.MM.YYYY"
                      )
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="İmza Tarihi">
                  {store.record?.dateOfSign
                    ? moment(store.record?.dateOfSign).format("DD.MM.YYYY")
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Açıklama" span={3}>
                  {store.record?.description}
                </Descriptions.Item>
              </Descriptions>
            </Element>
            <Element name="2">
              <Title level={4} style={{ marginTop: 24 }}>
                Kiracı
              </Title>
              <Descriptions size="small">
                <Descriptions.Item label="Ad Soyad">
                  {store.record?.partner.name}&nbsp;
                  {store.record?.partner.surname}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<FontAwesomeIcon icon={["fas", "map-marker-alt"]} />}
                >
                  {store.record?.partner.address}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<FontAwesomeIcon icon={["fas", "envelope"]} />}
                >
                  {store.record?.partner.email}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<FontAwesomeIcon icon={["fas", "phone"]} />}
                >
                  {store.record?.partner.phone}
                </Descriptions.Item>
                <Descriptions.Item
                  label={<FontAwesomeIcon icon={["fas", "id-badge"]} />}
                >
                  {store.record?.partner.identityNumber}
                </Descriptions.Item>
              </Descriptions>
            </Element>
            <Element name="3">
              <Title level={4} style={{ marginTop: 24 }}>
                Kiralanan Alanlar
              </Title>
              <ContractRentableAreas
                rentableareas={store.record?.rentableAreas}
              />
            </Element>
            <Element name="4">
              <Title level={4} style={{ marginTop: 24 }}>
                Koşullar
              </Title>
              <ContractConditionList
                conditions={store.record?.conditions}
                mode={DISPLAY_MODE.READ_ONLY}
              />
            </Element>
            <Element name="5">
              <div
                style={{
                  marginTop: 24,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Title level={4}>Nakit Akışı</Title>
                <Button
                  onClick={() => setSummaryModalVisible(true)}
                  size="small"
                >
                  Özet
                </Button>
              </div>
              <CashFlow cashFlow={store.record?.cashFlows} />
            </Element>
            {store.record?.type?.rule?.code === RULES.RULE0001 ? null : (
              <Element name="6">
                <Title level={4} style={{ marginTop: 24 }}>
                  Tahsilatlar
                </Title>
                <ContractPayments
                  payments={payments}
                  contract={store.record}
                  formVisibility={paymentFormVisibility}
                  handleFormVisibility={(visible) =>
                    setPaymentFormVisibility(visible)
                  }
                  onRefresh={(data) => {
                    setPayments(data);
                  }}
                  mode={
                    store.record?.contractStatu === "ACTIVE" &&
                    AuthCheck(abilities, AUTH_ACTIONS.WRITE, "Sözleşmeler") &&
                    !archived
                      ? DISPLAY_MODE.EDIT
                      : DISPLAY_MODE.READ_ONLY
                  }
                />
              </Element>
            )}
            {store.record?.type?.rule?.code === RULES.RULE0001 ? null : (
              <Element name="7">
                <Title level={4} style={{ marginTop: 24 }}>
                  Borç Bilgisi
                </Title>
                <ContractInterests
                  payments={payments || []}
                  cashFlow={store.record?.cashFlows || []}
                />
              </Element>
            )}
            <Element name="8">
              <Title level={4} style={{ marginTop: 24 }}>
                Ekler
              </Title>
              <Documents
                documents={store.record?.documents}
                mode={DISPLAY_MODE.READ_ONLY}
              />
            </Element>
          </div>
          <Modal
            footer={null}
            title="Nakit Akış Özeti"
            visible={summaryModalVisible}
            onCancel={() => setSummaryModalVisible(false)}
          >
            <CashFlowSummary cashflow={store.record?.cashFlows} />
          </Modal>
        </Spin>
      ) : (
        <FourOThree />
      )}
    </>
  );
}
